import { ConfirmModal, Spin } from "@/components";
import { PageWrapper } from "@/components/PageWrapper";
import * as GlobalS from "@/components/styled";
import { compareStrings, routes } from "@/helpers";
import { useToggle } from "@/hooks";
import {
  useDeleteApprovalWorkflowMutation,
  useGetApprovalWorkflowsQuery,
} from "@/services";
import {
  faCirclePlus,
  faEdit,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Space, Table } from "antd";
import { useState } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useProjectTypes } from "./hooks";

const ApprovalWorkflows = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const [deleteConfirmationDialogHidden, toggleDeleteConfirmationDialogHidden] =
    useToggle(true);
  const projectTypes = useProjectTypes();

  const [pendingItemToDelete, setPendingItemToDelete] = useState({});

  const {
    refetch,
    data: approvalWorkflows,
    isFetching,
  } = useGetApprovalWorkflowsQuery();
  const [deleteApprovalWorkflow] = useDeleteApprovalWorkflowMutation();

  const columns = [
    {
      title: intl.formatMessage({ id: "approvalWorkFlows.gridColumns.name" }),
      dataIndex: "name",
      sorter: {
        compare: (a, b) => compareStrings(a.name, b.name),
      },
    },
    {
      title: intl.formatMessage({
        id: "approvalWorkFlows.gridColumns.projectType",
      }),
      dataIndex: "projectTypeIds",
      render: (projectTypeIds) => {
        if (!projectTypeIds) return "";

        return projectTypes
          .filter(({ value }) => projectTypeIds.includes(value))
          .map(({ label }) => label)
          .join(", ");
      },
    },
    {
      title: intl.formatMessage({
        id: "general.actions",
      }),
      render: (approvalWorkFlow) => (
        <>
          <Link to={routes.admin.approvalWorkFlows.get(approvalWorkFlow.id)}>
            <Button
              data-test-id={`"approval-work-flows-row-edit-link-"${approvalWorkFlow.id}`}
              type="link"
              icon={<FontAwesomeIcon icon={faEdit} />}
            />
          </Link>
          <Button
            data-test-id={`"approval-work-flows-row-delete-button-"${approvalWorkFlow.id}`}
            type="link"
            onClick={() => handleModalToggle(approvalWorkFlow)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </>
      ),
    },
  ];

  const handleModalToggle = (item) => {
    setPendingItemToDelete(item);
    toggleDeleteConfirmationDialogHidden();
  };

  const handleDelete = () => {
    deleteApprovalWorkflow(pendingItemToDelete)
      .unwrap()
      .then(() => {
        toggleDeleteConfirmationDialogHidden();
        refetch();
        setPendingItemToDelete({});
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
      });
  };

  if (isFetching) return <Spin />;

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "approvalWorkFlows.title" })}
    >
      <GlobalS.PageContentCard
        title={intl.formatMessage({ id: "approvalWorkFlows.title" })}
        extra={
          <>
            <Space>
              <Button
                data-test-id="project-roles-add"
                type="primary"
                icon={<FontAwesomeIcon icon={faCirclePlus} />}
                onClick={() => navigate(routes.admin.approvalWorkFlows.add)}
              >
                {intl.formatMessage({ id: "general.add" })}
              </Button>
              <ConfirmModal
                onSaveChanges={handleDelete}
                hidden={deleteConfirmationDialogHidden}
                toggle={toggleDeleteConfirmationDialogHidden}
                headerTitleKey={"approvalWorkFlows.deleteWorkFlowModalTitle"}
                bodyTextKey={"approvalWorkFlows.deleteWorkFlowModalDescription"}
                confirmationTimeoutSeconds={5}
              />
            </Space>
          </>
        }
      >
        <Table
          data-test-id="approval-work-flows-table"
          rowKey="id"
          pagination={true}
          columns={columns}
          dataSource={approvalWorkflows}
          loading={isFetching}
        />
      </GlobalS.PageContentCard>
    </PageWrapper>
  );
};

export default ApprovalWorkflows;
